import { DecafClient } from '@decafhub/decaf-client';
import { DecafContextType } from '@decafhub/decaf-react';
import Decimal from 'decimal.js';
import Cookies from 'js-cookie';
import md5 from 'md5';
import { useLayoutEffect, useRef, useState } from 'react';
import { Params } from 'react-router-dom';

const FOOTER_HEIGHT = 32;
const FOOTER_PADDING = 16;

/**
 * This hook is used to calculate the remaining height of an element.
 * It takes into account the footer height and padding.
 *
 * @param elementId the id of the element to calculate the available height.
 * @returns the remaining height of the element.
 */
export function useRemaningHeight(elementId: string): number {
  const [remainingHeight, setRemainingHeight] = useState<number>(0);

  useLayoutEffect(() => {
    const e = document.getElementById(elementId);

    const calculate = () => {
      if (!e) {
        return;
      }

      const elementTop = e.getBoundingClientRect().top;
      const elementHeight = window.innerHeight - elementTop - FOOTER_HEIGHT - FOOTER_PADDING;
      setRemainingHeight(elementHeight);
    };

    calculate();
    e?.addEventListener('resize', calculate);
    window.addEventListener('resize', calculate);

    return () => {
      window.removeEventListener('resize', calculate);
      e?.removeEventListener('resize', calculate);
    };
  }, [elementId]);

  return remainingHeight;
}

/**
 * This hook is used to calculate the max possible height of a table.
 * It is used to set the height of the table to make it scrollable
 * when the content is too large.
 * @param elementId the id of the element that contains the table.
 * @param bottomSpace extra space to be added to the bottom of the table container.
 * @returns the max height of the table.
 * @example
 * ```tsx
 * const maxHeight = useTableMaxHeight('table-container', 50);
 * return (
 *  <Table
      {...tableProps}
      id={'table-container'}
      scroll={{
        x: 'max-content',
        y: maxHeight,
      }}
    />
* );
* ```
**/
export function useTableMaxHeight(elementId: string, bottomSpace: number = 0): string | number {
  const [maxHeight, setMaxHeight] = useState<string | number>(400);
  const remaining = useRemaningHeight(elementId);
  const observer = useRef<ResizeObserver | null>(null);

  useLayoutEffect(() => {
    const w = document.getElementById(elementId);
    if (!w) {
      process.env.NODE_ENV === 'development' &&
        console.warn(`useTableMaxHeight: Element with id ${elementId} not found. Aborting...`);
      return;
    }

    observer.current = new ResizeObserver((e) => {
      let max = 0;

      e.forEach((tableWrapper) => {
        const tHeaderHeight = getElementComputedHeight(tableWrapper.target.querySelector('.ant-table-header'));
        const tFooterHeight = getElementComputedHeight(tableWrapper.target.querySelector('.ant-table-footer'));
        const tContainerOffset = getElementTotalOffset(tableWrapper.target.querySelector('.ant-table-container'));
        const paginations = tableWrapper.target.querySelectorAll('.ant-pagination') ?? [];
        const paginationHeights = Array.from(paginations).reduce((acc, cur) => acc + getElementComputedHeight(cur), 0);

        max = remaining - tHeaderHeight - tFooterHeight - tContainerOffset - paginationHeights - bottomSpace;
      });

      setMaxHeight(max > 350 ? max : '100%');
    });

    observer.current.observe(w?.closest('.ant-table-wrapper')!);

    return () => {
      observer.current?.disconnect();
    };
  }, [bottomSpace, elementId, remaining]);

  return maxHeight;
}

export function getElementComputedHeight(element?: Element | null): number {
  if (!element) {
    return 0;
  }

  const height = element.getBoundingClientRect().height;
  const offset = getElementTotalOffset(element);
  return Math.ceil(height + offset);
}

export function getElementTotalOffset(element?: Element | null): number {
  if (!element) {
    return 0;
  }

  const height = element.getBoundingClientRect().height;
  const style = window.getComputedStyle(element);
  const marginTop = parseFloat(style.marginTop || '0');
  const marginBottom = parseFloat(style.marginBottom || '0');

  return Math.ceil(height - element.clientHeight + marginTop + marginBottom);
}

export function lightenDarkenColor(col: string, amt: number) {
  let usePound = false;

  if (col[0] === '#') {
    col = col.slice(1);
    usePound = true;
  }

  const num = parseInt(col, 16);

  let r = (num >> 16) + amt;

  if (r > 255) r = 255;
  else if (r < 0) r = 0;

  let b = ((num >> 8) & 0x00ff) + amt;

  if (b > 255) b = 255;
  else if (b < 0) b = 0;

  let g = (num & 0x0000ff) + amt;

  if (g > 255) g = 255;
  else if (g < 0) g = 0;

  return (usePound ? '#' : '') + String('000000' + (g | (b << 8) | (r << 16)).toString(16)).slice(-6);
}

export function logout(client: DecafClient, fromAll?: boolean) {
  client.barista
    .get(`/auth/${fromAll ? 'logoutall' : 'logout'}`, { timeout: 3000 })
    .catch((e) => {
      if (fromAll) {
        console.error(e);
        alert('Failed to logout from all devices. Please try to logout from each device individually.');
      }
    })
    .finally(() => {
      const DECAF_AUTH_COOKIE_NAME = 'ember_simple_auth-session';
      Cookies.remove(DECAF_AUTH_COOKIE_NAME);
      window.location.reload();
    });
}

/**
 * Follow an `href` from a menu/dropdown/palette item with the same heuristic
 * everywhere: same-origin paths trigger a full-page navigation in the same
 * tab (typical "go to sibling webapp" use case); cross-origin URLs open in a
 * new tab. Consumers who explicitly want a new tab for a same-origin URL
 * should use `onClick`/`onSelect` and call `window.open` themselves.
 */
export function followHref(href: string): void {
  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return;
  }
  if (url.origin === window.location.origin) {
    window.location.assign(href);
  } else {
    window.open(href, '_blank', 'noopener,noreferrer');
  }
}

export function getGravatarUrl(email: string): Promise<string | undefined> {
  const url = `https://www.gravatar.com/avatar/${md5(email)}`;

  return fetch(`${url}?d=404`, { method: 'HEAD' })
    .then((response) => (response.status !== 200 ? undefined : url))
    .catch(() => undefined);
}

export type BooleanMap<T> = { True: T; False: T };

export function booleanMap<T>(map: BooleanMap<T>): (x: boolean) => T {
  return (x) => (x ? map.True : map.False);
}

export type NullableBooleanMap<T> = { Null: T } & BooleanMap<T>;

export function nullableBooleanMap<T>(map: NullableBooleanMap<T>): (x?: boolean | undefined | null) => T {
  return (x) => (x == null ? map.Null : booleanMap(map)(x));
}

export enum Direction {
  Short = -1,
  Square = 0,
  Long = 1,
}

export function toDirection(x: number | Decimal | Direction): Direction {
  switch (new Decimal(0).comparedTo(x)) {
    case 1:
      return Direction.Short;
    case 0:
      return Direction.Square;
    case -1:
      return Direction.Long;
  }
  return Direction.Square;
}

export type DirectionMap<T> = { Short: T; Square: T; Long: T };

export function directionMap<T>(map: DirectionMap<T>): (x: number | Decimal | Direction) => T {
  return (x) => {
    switch (toDirection(x)) {
      case Direction.Short:
        return map.Short;
      case Direction.Square:
        return map.Square;
      case Direction.Long:
        return map.Long;
    }
  };
}

export function setAtKey<T>(array: T[], items: T[], key: keyof T): T[] {
  const result: T[] = [...array];

  items.forEach((item) => {
    const index = result.findIndex((r) => r[key] === item[key]);

    if (index >= 0) {
      result[index] = item;
    } else {
      result.push(item);
    }
  });

  return result;
}

export type DecafLoaderFactory<T> = (context: DecafContextType) => DecafLoader<T>;

export type DecafLoader<T> = (args: { request: Request; params: Params; context?: any }) => Promise<T>;

export function getSearchParams(request: Request): URLSearchParams {
  return new URL(request.url).searchParams;
}

export function getSearchParam(request: Request, name: string): string | null {
  return new URL(request.url).searchParams.get(name);
}

export function getSearchParamDefault(request: Request, name: string, defaultValue: string): string {
  return new URL(request.url).searchParams.get(name) ?? defaultValue;
}

export function getSearchParamAll(request: Request, name: string): string[] {
  return new URL(request.url).searchParams.getAll(name);
}
